import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, CheckCircle2, XCircle, Mail } from "lucide-react";

// Confirms an email-verification token (POST /api/auth/verify-email). The token is
// read from the URL, used once, and NEVER rendered/logged. On success the account
// status is refreshed from the backend so the banner disappears without re-login.
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [state, setState] = useState("loading"); // loading | success | invalid | network
  const [email, setEmail] = useState("");
  const [resend, setResend] = useState("idle"); // idle | sending | sent | limited | error
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) { setState("invalid"); return; }
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setState("success");
        try { await refresh(); } catch {}
      } catch (e) {
        if (e?.response) setState("invalid");
        else setState("network");
      }
    })();
  }, [params, refresh]);

  const doResend = async () => {
    const em = user?.email || email;
    if (!em) return;
    setResend("sending");
    try {
      await api.post("/auth/resend-verification", { email: em });
      setResend("sent");
    } catch (e) {
      setResend(e?.response?.status === 429 ? "limited" : "error");
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary/30 p-4" data-testid="verify-email-page">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 text-center shadow-xl">
        {state === "loading" && (
          <div data-testid="verify-loading">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-accent" />
            <p className="mt-4 text-sm text-muted-foreground">{t("ev.page.verifying")}</p>
          </div>
        )}

        {state === "success" && (
          <div data-testid="verify-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
            <h1 className="mt-4 font-display text-xl font-bold">{t("ev.page.title")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{t("ev.page.success")}</p>
            <Link to="/dashboard" data-testid="verify-go-dashboard"
              className="mt-6 inline-flex items-center justify-center rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-accent">
              {t("ev.page.go_dash")}
            </Link>
          </div>
        )}

        {(state === "invalid" || state === "network") && (
          <div data-testid="verify-error">
            <XCircle className="mx-auto h-12 w-12 text-destructive" />
            <h1 className="mt-4 font-display text-xl font-bold">{t("ev.page.title")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {state === "network" ? t("ev.page.network") : t("ev.page.invalid")}
            </p>

            {!user && (
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="verify-resend-email" placeholder={t("ev.email_ph")}
                className="mt-5 w-full rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/50" />
            )}

            <button onClick={doResend} disabled={resend === "sending" || (!user && !email)}
              data-testid="verify-resend-btn"
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-accent disabled:opacity-50">
              {resend === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {t("ev.page.resend")}
            </button>

            {resend === "sent" && <p className="mt-3 text-sm text-emerald-500" data-testid="verify-resend-sent">{t("ev.banner.sent")}</p>}
            {resend === "limited" && <p className="mt-3 text-sm text-amber-500" data-testid="verify-resend-limited">{t("ev.banner.limited")}</p>}
            {resend === "error" && <p className="mt-3 text-sm text-destructive" data-testid="verify-resend-error">{t("ev.banner.error")}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
